import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

// Inactivity timeout: after IDLE_TIMEOUT_MS without any activity a warning appears.
// If the user does not answer within WARNING_MS they are signed out automatically.
export const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
export const WARNING_MS = 10 * 1000;

// Last activity is shared through localStorage so several open tabs act as one session:
// being active in any tab keeps every tab signed in.
const ACTIVITY_KEY = "asiatech_last_activity";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
];
const ACTIVITY_WRITE_INTERVAL_MS = 1000;

const readLastActivity = () => {
  try {
    const value = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const writeLastActivity = (timestamp) => {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(timestamp));
  } catch {
    // Storage can be unavailable (private mode); the in-memory timestamp still works.
  }
};

const IdleLogout = () => {
  const { user, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const warningShownRef = useRef(false);
  const lastWriteRef = useRef(0);
  const memoryActivityRef = useRef(Date.now());

  const markActive = useCallback(() => {
    const now = Date.now();
    memoryActivityRef.current = now;
    lastWriteRef.current = now;
    writeLastActivity(now);
  }, []);

  const hideWarning = useCallback(() => {
    warningShownRef.current = false;
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!user) {
      warningShownRef.current = false;
      setSecondsLeft(null);
      return undefined;
    }

    // Signing in (or loading the app while signed in) counts as activity.
    markActive();

    const onActivity = () => {
      // Once the warning is up, only its buttons can extend the session.
      if (warningShownRef.current) return;
      if (Date.now() - lastWriteRef.current < ACTIVITY_WRITE_INTERVAL_MS) return;
      markActive();
    };

    // Compares timestamps instead of counting timers, so a sleeping laptop or a
    // throttled background tab cannot stretch the timeout.
    const check = () => {
      const lastActivity = readLastActivity() ?? memoryActivityRef.current;
      const idleFor = Date.now() - lastActivity;

      if (idleFor >= IDLE_TIMEOUT_MS + WARNING_MS) {
        logout("idle_timeout");
        window.location.replace("/");
        return;
      }

      if (idleFor >= IDLE_TIMEOUT_MS) {
        warningShownRef.current = true;
        setSecondsLeft(
          Math.max(
            1,
            Math.ceil((IDLE_TIMEOUT_MS + WARNING_MS - idleFor) / 1000),
          ),
        );
      } else if (warningShownRef.current) {
        // The user became active in another tab.
        hideWarning();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, onActivity, {
        passive: true,
        capture: true,
      }),
    );
    document.addEventListener("visibilitychange", check);
    const intervalId = window.setInterval(check, 1000);
    check();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, onActivity, { capture: true }),
      );
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(intervalId);
    };
  }, [user, logout, markActive, hideWarning]);

  if (!user || secondsLeft === null) return null;

  const handleStay = () => {
    markActive();
    hideWarning();
  };

  const handleLogoutNow = () => {
    logout("manual");
    window.location.replace("/");
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-timeout-title"
        aria-describedby="idle-timeout-description"
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
      >
        <h2
          id="idle-timeout-title"
          className="text-lg font-extrabold text-[#DF2E1B]"
        >
          Session timeout
        </h2>
        <p
          id="idle-timeout-description"
          className="mt-3 text-sm leading-relaxed text-navy/75"
        >
          Your session will expire in{" "}
          <span className="font-bold text-navy">{secondsLeft}</span>{" "}
          {secondsLeft === 1 ? "second" : "seconds"} due to inactivity. Do you
          want to stay logged in?
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="btn-secondary px-4 py-2 text-sm"
            onClick={handleLogoutNow}
          >
            No, log out now
          </button>
          <button
            type="button"
            autoFocus
            className="btn-primary px-4 py-2 text-sm"
            onClick={handleStay}
          >
            Yes, keep me logged in
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleLogout;
